// Editor for `Model.currency`.
//
// USD is the hub for every conversion, so each currency the model knows about
// needs exactly one number: how many units of it equal 1 USD. That gives two
// shapes of setup:
//
//   - Base is USD (the default): optionally add display currencies so outputs
//     can be shown in them.
//   - Base is something else: the rate for that currency is required, since it
//     is the only path back to USD for both display and portfolio reporting.
//
// Each rate row shows both directions and a worked example, so there is no
// ambiguity about which way the number goes.

import { AlertTriangle, Plus, X } from 'lucide-react';
import {
  CURRENCIES,
  currencyLabel,
  currencySymbol,
  currencyConfigIssues,
  displayCurrencyOptions,
  modelCurrency,
} from '@rc/engine';
import type { CurrencyCode, CurrencyRate, Model } from '@rc/engine/types';
import { fmtMoneyExact } from '../../lib/format';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

const USD = 'USD';
const EXAMPLE_AMOUNT = 1_000_000;

export default function CurrencyEditor({ model, onChange }: Props) {
  const cfg = modelCurrency(model);
  const base = cfg.base;
  const rates = cfg.rates;
  const issues = currencyConfigIssues(model);
  const options = displayCurrencyOptions(model);

  const write = (patch: Partial<typeof cfg>) =>
    onChange({ ...model, currency: { ...cfg, ...patch } });

  const setBase = (next: CurrencyCode) => {
    if (next === base) return;
    // Seed a rate row for a non-USD base so the required input appears filled
    // in with a placeholder the user can correct, rather than silently missing.
    const needsRate = next !== USD && !rates.some((r) => r.code === next);
    const nextRates = needsRate ? [...rates, { code: next, perUsd: 1 }] : rates;
    // Drop a display default that the new base can no longer reach.
    const keepDefault =
      cfg.displayDefault === next ||
      (cfg.displayDefault && nextRates.some((r) => r.code === cfg.displayDefault));
    write({
      base: next,
      rates: nextRates,
      displayDefault: keepDefault ? cfg.displayDefault : undefined,
    });
  };

  const updateRate = (i: number, patch: Partial<CurrencyRate>) => {
    const next = [...rates];
    next[i] = { ...next[i], ...patch };
    write({ rates: next });
  };

  const removeRate = (i: number) => {
    const removed = rates[i]?.code;
    const next = rates.filter((_, j) => j !== i);
    write({
      rates: next,
      displayDefault: cfg.displayDefault === removed ? undefined : cfg.displayDefault,
    });
  };

  const addRate = () => {
    const used = new Set<CurrencyCode>([USD, ...rates.map((r) => r.code)]);
    const candidate = CURRENCIES.find((c) => !used.has(c.code));
    write({ rates: [...rates, { code: candidate?.code || '', perUsd: 1 }] });
  };

  // The base currency's own rate is the required "how do we get to USD" input,
  // so it is pulled out of the generic list and given its own field.
  const baseRateIndex = base === USD ? -1 : rates.findIndex((r) => r.code === base);
  const extraRates = rates
    .map((rate, index) => ({ rate, index }))
    .filter(({ index }) => index !== baseRateIndex);

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Every monetary value on this model — cost per role, spend per FTE, budget buckets, and
        scenario budgets — is entered in the <strong>base currency</strong>. Conversions are quoted
        against USD, so each additional currency needs one rate: how many units equal 1 USD.
      </p>

      {issues.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-xs font-semibold text-amber-800">
                {issues.length === 1 ? 'Fix this before saving' : 'Fix these before saving'}
              </p>
              <ul className="text-xs text-amber-800 space-y-0.5 list-disc pl-4">
                {issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 space-y-3">
        <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
          <div>
            <label className="label !mb-0.5">Model base currency</label>
            <p className="text-xs text-slate-500 max-w-sm mb-1.5">
              The currency this model's rate tables are already written in.
            </p>
            <select
              className="input py-1 px-2 w-64"
              value={base}
              onChange={(e) => setBase(e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {currencyLabel(c.code)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label !mb-0.5">Default output currency</label>
            <p className="text-xs text-slate-500 max-w-sm mb-1.5">
              What scenario outputs open in. Viewers can switch temporarily.
            </p>
            <select
              className="input py-1 px-2 w-64"
              value={cfg.displayDefault && options.includes(cfg.displayDefault) ? cfg.displayDefault : base}
              onChange={(e) =>
                write({ displayDefault: e.target.value === base ? undefined : e.target.value })
              }
            >
              {options.map((code) => (
                <option key={code} value={code}>
                  {currencyLabel(code)}
                  {code === base ? ' (base)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {base !== USD && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
            Changing the base currency re-labels the existing numbers in this model — it does not
            rescale them. Update the rate tables yourself if the values were entered in a different
            currency.
          </p>
        )}
      </div>

      {base !== USD && baseRateIndex >= 0 && (
        <div className="rounded-lg border border-slate-200 px-4 py-3 space-y-2">
          <div>
            <label className="label !mb-0.5">
              {base} rate to USD <span className="text-rose-600">*</span>
            </label>
            <p className="text-xs text-slate-500 max-w-xl">
              Required. This model's values are in {base}, and this rate is the only way to show
              them in USD or include them in portfolio reporting.
            </p>
          </div>
          <RateInput
            code={base}
            perUsd={rates[baseRateIndex]?.perUsd}
            asOf={rates[baseRateIndex]?.asOf}
            base={base}
            onChange={(patch) => updateRate(baseRateIndex, patch)}
          />
        </div>
      )}

      <div className="rounded-lg border border-slate-200 px-4 py-3 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <label className="label !mb-0.5">
              {base === USD ? 'Display currencies' : 'Additional display currencies'}
            </label>
            <p className="text-xs text-slate-500 max-w-xl">
              Optional. Add a currency here to let outputs be displayed in it. Outputs only ever
              offer the currencies set up on this page.
            </p>
          </div>
          <button type="button" className="btn-secondary shrink-0" onClick={addRate}>
            <Plus size={14} /> Add currency
          </button>
        </div>

        {extraRates.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            None. Outputs will only be shown in {base}
            {base !== USD ? ' and USD' : ''}.
          </p>
        ) : (
          <div className="space-y-2.5">
            {extraRates.map(({ rate, index }) => (
              <div
                key={index}
                className="flex items-start gap-2 border-t border-slate-100 pt-2.5 first:border-0 first:pt-0"
              >
                <select
                  className="input py-1 px-2 w-48 shrink-0"
                  value={rate.code}
                  onChange={(e) => updateRate(index, { code: e.target.value })}
                >
                  {rate.code && !CURRENCIES.some((c) => c.code === rate.code) && (
                    <option value={rate.code}>{rate.code}</option>
                  )}
                  {CURRENCIES.filter(
                    (c) => c.code === rate.code || (c.code !== USD && c.code !== base && !rates.some((r) => r.code === c.code)),
                  ).map((c) => (
                    <option key={c.code} value={c.code}>
                      {currencyLabel(c.code)}
                    </option>
                  ))}
                </select>
                <div className="min-w-0 flex-1">
                  <RateInput
                    code={rate.code}
                    perUsd={rate.perUsd}
                    asOf={rate.asOf}
                    base={base}
                    onChange={(patch) => updateRate(index, patch)}
                  />
                </div>
                <button
                  type="button"
                  className="text-slate-400 hover:text-rose-600 mt-1.5 shrink-0"
                  onClick={() => removeRate(index)}
                  aria-label={`Remove ${rate.code || 'currency'}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A single USD-quoted rate. Shows both directions plus a worked example so the
 * number's meaning is unambiguous regardless of which way the user thinks.
 */
function RateInput({
  code,
  perUsd,
  asOf,
  base,
  onChange,
}: {
  code: CurrencyCode;
  perUsd: number | undefined;
  asOf: string | undefined;
  base: CurrencyCode;
  onChange: (patch: Partial<CurrencyRate>) => void;
}) {
  const usable = typeof perUsd === 'number' && Number.isFinite(perUsd) && perUsd > 0;

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-500 tabular-nums">1 USD =</span>
        <input
          type="number"
          step="any"
          min={0}
          className="input py-1 px-2 w-32 text-right tabular-nums"
          // A cleared or unparseable rate is stored as NaN, which must not reach
          // the DOM as a value.
          value={typeof perUsd === 'number' && Number.isFinite(perUsd) ? perUsd : ''}
          onChange={(e) => {
            const raw = e.target.value;
            onChange({ perUsd: raw.trim() === '' ? Number.NaN : Number(raw) });
          }}
        />
        <span className="text-sm font-medium text-slate-700">{code || '—'}</span>
        <input
          type="text"
          className="input py-1 px-2 w-44 ml-2"
          placeholder="Rate source / as of"
          value={asOf ?? ''}
          onChange={(e) => onChange({ asOf: e.target.value || undefined })}
        />
      </div>

      {usable && code ? (
        <p className="text-[11px] text-slate-500 tabular-nums">
          1 {code} = {(1 / perUsd!).toFixed(4)} USD
          <span className="text-slate-300 mx-1.5">·</span>
          {base === code ? (
            <>
              {fmtMoneyExact(EXAMPLE_AMOUNT, code)} displays as{' '}
              {fmtMoneyExact(EXAMPLE_AMOUNT / perUsd!, USD)}
            </>
          ) : (
            <>
              {fmtMoneyExact(EXAMPLE_AMOUNT, USD)} displays as{' '}
              {fmtMoneyExact(EXAMPLE_AMOUNT * perUsd!, code)}
            </>
          )}
        </p>
      ) : (
        <p className="text-[11px] text-rose-600">
          Enter a rate greater than zero{code ? ` for ${currencySymbol(code)}${code}` : ''}.
        </p>
      )}
    </div>
  );
}
